import type { App } from '@/types/app';
import type { Catalog } from '@/types/catalog';
import type { Container } from '@/types/container';
import type { Endpoint } from '@/types/endpoint';
import type { ExternalUser } from '@/types/externalUser';

const labelCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export type EntityKind = 'App' | 'Endpoint' | 'Catalog' | 'External user' | 'Container';

export interface EntityDoc {
  id: string;
  kind: EntityKind;
  label: string;
  sublabel?: string;
  to: string;
}

interface Sources {
  apps?: App[];
  endpoints?: Endpoint[];
  catalogs?: Catalog[];
  users?: ExternalUser[];
  containers?: Container[];
}

function collect<T>(
  out: EntityDoc[],
  items: T[] | undefined,
  kind: EntityKind,
  map: (item: T) => { rawId: number | string; label?: string; sublabel?: string; to: string },
) {
  if (!Array.isArray(items)) return;
  for (const item of items) {
    const m = map(item);
    if (!m.label) continue;
    out.push({ id: `${kind}:${m.rawId}`, kind, label: m.label, sublabel: m.sublabel || undefined, to: m.to });
  }
}

export function buildEntityDocs(src: Sources): EntityDoc[] {
  const out: EntityDoc[] = [];
  collect(out, src.apps, 'App', (a) => ({ rawId: a.id, label: a.display_name || a.name, sublabel: a.full_subdomain, to: `/apps/${a.id}` }));
  collect(out, src.endpoints, 'Endpoint', (e) => ({ rawId: e.id, label: e.name, sublabel: e.url, to: `/resources/endpoints/${e.id}` }));
  collect(out, src.catalogs, 'Catalog', (c) => ({ rawId: c.id, label: c.name, sublabel: c.description, to: `/catalog/${c.id}` }));
  // External user detail route is keyed by the numeric row id, not external_user_id.
  collect(out, src.users, 'External user', (u) => ({
    rawId: u.id,
    label: u.display_name || u.external_user_id,
    sublabel: u.external_user_id,
    to: `/external-users/${u.id}`,
  }));
  collect(out, src.containers, 'Container', (c) => ({ rawId: c.id, label: c.name, sublabel: c.image, to: `/resources/containers/${c.id}` }));
  return out;
}

export function searchEntities(docs: EntityDoc[], query: string, opts?: { perKind?: number; total?: number }): EntityDoc[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const perKind = opts?.perKind ?? 4;
  const total = opts?.total ?? 12;

  const ranked: Array<{ doc: EntityDoc; rank: number }> = [];
  for (const doc of docs) {
    const label = doc.label.toLowerCase();
    let rank: number;
    if (label.startsWith(q)) rank = 0;
    else if (label.includes(q)) rank = 1;
    else if (doc.sublabel?.toLowerCase().includes(q)) rank = 2;
    else continue;
    ranked.push({ doc, rank });
  }
  ranked.sort(
    (a, b) => a.rank - b.rank || labelCollator.compare(a.doc.label, b.doc.label),
  );

  const counts = new Map<EntityKind, number>();
  const out: EntityDoc[] = [];
  for (const { doc } of ranked) {
    if (out.length >= total) break;
    const n = counts.get(doc.kind) ?? 0;
    if (n >= perKind) continue;
    counts.set(doc.kind, n + 1);
    out.push(doc);
  }
  return out;
}
